import { useZero } from "@rocicorp/zero/react";
import { useMemo, useState } from "react";
import { isManagedAiConnectorId } from "../../../shared/aiProviders/managedAiConnector";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "../../hooks/useQuery";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { spaceIcon } from "../../lib/spaceIcon";
import {
  FormCard,
  IconContainer,
  RowLabel,
  SectionHeading,
  SpendList,
  SpendRow,
  UsdInput,
} from "../AiProviderModal/modalForm";
import { Icon, type IconName } from "../Icon";
import { Text } from "../Text";
import { managedAiLifetimeCreditCents } from "../useSessionBootstrap";

const TOTAL = "total";
const inputStyle = { width: 160 } as const;

type ScopeRow = {
  key: string;
  spaceId: string | null;
  label: string;
  icon: IconName;
};

type Props = {
  connectorId: string;
};

export function UsageLimitsSection({ connectorId }: Props) {
  const z = useZero();
  const canManage = useHasAccess("tenant:ai_providers_manage");
  const lifetimeCreditCents = managedAiLifetimeCreditCents();
  const hasLifetimeCredit =
    lifetimeCreditCents != null && isManagedAiConnectorId(connectorId);
  const lifetimeCreditUsd =
    lifetimeCreditCents == null ? null : (lifetimeCreditCents / 100).toFixed(2);
  const [budgets] = useQuery(queries.monitoringUsageBudgets());
  const [spaces] = useQuery(queries.spacesLite({ includeAdminSpaces: true }));
  const displaySpaceName = useSpaceDisplayName();
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const scopeRows = useMemo<ScopeRow[]>(() => {
    const teamSpaces = (spaces ?? [])
      .filter((space) => space.mode !== "personal")
      .map((space) => ({
        key: space.id,
        spaceId: space.id as string | null,
        label: displaySpaceName(space),
        icon: spaceIcon(space.mode),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      {
        key: TOTAL,
        spaceId: null,
        label: hasLifetimeCredit ? "30-day total limit" : "Total spend",
        icon: "wallet" as const,
      },
      ...teamSpaces,
    ];
  }, [spaces, displaySpaceName, hasLifetimeCredit]);

  const budgetByScope = useMemo(() => {
    const map = new Map<string, { id: string; limitAmount: number }>();
    for (const budget of budgets ?? []) {
      if (budget.connectorId !== connectorId) continue;
      if (budget.measure !== "spend_usd") continue;
      map.set(budget.spaceId ?? TOTAL, budget);
    }
    return map;
  }, [budgets, connectorId]);

  const valueFor = (scope: ScopeRow) => {
    const draft = drafts[scope.key];
    if (draft != null) return draft;
    const limit = budgetByScope.get(scope.key)?.limitAmount;
    return limit == null ? "" : String(limit);
  };

  const commit = (scope: ScopeRow) => {
    if (!canManage) return;
    const budget = budgetByScope.get(scope.key);
    const raw = valueFor(scope).trim();
    const amount = raw === "" ? 0 : Number.parseFloat(raw);
    if (!Number.isFinite(amount)) return;
    if (amount <= 0) {
      if (budget) z.mutate(sharedMutators.deleteUsageBudget({ id: budget.id }));
      return;
    }
    if (budget?.limitAmount === amount) return;
    z.mutate(
      sharedMutators.updateUsageBudget({
        connectorId,
        ...(scope.spaceId ? { spaceId: scope.spaceId } : {}),
        measure: "spend_usd",
        limitAmount: amount,
      })
    );
  };

  return (
    <FormCard>
      <SectionHeading
        title="Spend limits"
        description={
          hasLifetimeCredit
            ? `explore includes $${lifetimeCreditUsd} of managed AI usage for the lifetime of the tenant. Connect your own AI API key to continue. Optional 30-day limits can only stop usage sooner. Leave blank for no 30-day limit. Calls already in progress may finish above a limit.`
            : "Workflow builds and runs, monitor activity, and personal chats using this connector are blocked when the applicable 30-day spend limit is reached. Space limits exclude personal chats. Calls already in progress may finish above a limit. Leave blank for no limit."
        }
      />
      <SpendList>
        {hasLifetimeCredit && (
          <SpendRow>
            <IconContainer>
              <Icon name="wallet" size={16} />
            </IconContainer>
            <RowLabel>
              <Text
                variant="text-regular-md"
                color="var(--text-primary)"
                ellipsis
              >
                Lifetime credit
              </Text>
            </RowLabel>
            <UsdInput
              value={String(lifetimeCreditUsd)}
              disabled
              onChange={() => undefined}
              style={inputStyle}
            />
          </SpendRow>
        )}
        {scopeRows.map((scope) => (
          <SpendRow key={scope.key} onBlur={() => commit(scope)}>
            <IconContainer>
              <Icon name={scope.icon} size={16} />
            </IconContainer>
            <RowLabel>
              <Text
                variant="text-regular-md"
                color="var(--text-primary)"
                ellipsis
              >
                {scope.label}
              </Text>
            </RowLabel>
            <UsdInput
              value={valueFor(scope)}
              placeholder="0.00"
              disabled={!canManage}
              onChange={(next) =>
                setDrafts((prev) => ({ ...prev, [scope.key]: next }))
              }
              style={inputStyle}
            />
          </SpendRow>
        ))}
      </SpendList>
    </FormCard>
  );
}
